// Angle pictures (14 Oct 2026). An `angle` is two lines meeting at a point,
// with the angle between them (or the larger angle round the outside) marked.
// `angles_at_point` is several angles: round a point, on a straight line,
// where two lines cross, or in the corners of a triangle or a four-sided
// shape. A size is written "35°"; an unknown is "?" or a letter.
//
// A `transversal` (Year 7, 1 Nov 2026) is two parallel lines crossed by a
// third line, with the 8 angles it makes numbered 1 to 8, sized, lettered
// or left bare.
//
// Every angle is drawn its true size. An unknown angle is drawn the size
// that is left over, and is never named anywhere.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export type AngleLabel =
  | { kind: 'size'; degrees: number }
  // "?" has no letter.
  | { kind: 'unknown'; letter: string | null }
  // An angle's number, 1 to 8. Only on a transversal, where the angles are
  // talked about by number.
  | { kind: 'name'; name: string }
  // No label and no arc. Only where two lines cross.
  | { kind: 'none' };

export type AngleSpec = {
  type: 'angle';
  // The smaller angle between the two lines.
  degrees: number;
  label: Exclude<AngleLabel, { kind: 'none' } | { kind: 'name' }>;
  // The label and its arc sit on the larger side.
  reflex: boolean;
  protractor: boolean;
};

export type AnglesAtPointSpec = {
  type: 'angles_at_point';
  layout: 'point' | 'line' | 'crossing' | 'triangle' | 'quadrilateral';
  // In order: clockwise round a point, left to right on a line, clockwise
  // from the top where lines cross and in a triangle, clockwise from the top
  // left in a four-sided shape.
  angles: AngleLabel[];
  // The size each angle is drawn, in degrees.
  drawn: number[];
};

export type TransversalSpec = {
  type: 'transversal';
  // 8 angles: where the crossing line meets the top line, clockwise from
  // the top left (above left, above right, below right, below left), then
  // the same four where it meets the bottom line.
  angles: AngleLabel[];
  // The angle above the top line, to the left of the crossing line, as
  // drawn. Every other angle follows from it, as the lines are parallel.
  slant: number;
};

// More angles than this cannot be told apart on a phone.
const MAX_ANGLES = { point: 10, line: 8 };
// What the angles of each layout add up to.
const WHOLE = { point: 360, line: 180, triangle: 180, quadrilateral: 360 };
// The top angle where two lines cross and no size is given.
const CROSSING_DEFAULT = 65;

function readLabel(value: unknown): AngleLabel | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (text === '') return { kind: 'none' };
  if (text === '?') return { kind: 'unknown', letter: null };
  if (/^[a-zA-Z]$/.test(text)) return { kind: 'unknown', letter: text };
  const size = /^(\d{1,3}(?:\.\d+)?)°$/.exec(text);
  if (!size) return null;
  const degrees = Number(size[1]);
  return degrees > 0 && degrees < 360 ? { kind: 'size', degrees } : null;
}

export function readAngle(record: Record<string, unknown>): AngleSpec | null {
  const { degrees, mark, protractor } = record;
  // 180° and 360° are not drawn as an angle.
  if (typeof degrees !== 'number' || !(degrees > 0 && degrees < 180))
    return null;
  const label = readLabel(record.label);
  if (label === null || label.kind === 'none' || label.kind === 'name')
    return null;
  if (mark !== undefined && mark !== 'reflex') return null;
  if (protractor !== undefined && typeof protractor !== 'boolean') return null;
  const reflex = mark === 'reflex';
  // The child reads a protractor, so the size is never written beside it.
  if (protractor === true && (reflex || label.kind !== 'unknown')) return null;
  // A written size must be the size that is drawn.
  if (
    label.kind === 'size' &&
    label.degrees !== (reflex ? 360 - degrees : degrees)
  )
    return null;
  return {
    type: 'angle',
    degrees,
    label,
    reflex,
    protractor: protractor === true,
  };
}

// Opposite angles are equal, so one size fixes all four. With no size given
// the lines cross at a fixed slant.
function crossingSizes(angles: AngleLabel[]): number[] | null {
  if (angles.length !== 4) return null;
  let top: number | null = null;
  for (const [index, angle] of angles.entries()) {
    if (angle.kind !== 'size') continue;
    if (angle.degrees >= 180) return null;
    top ??= index % 2 === 0 ? angle.degrees : 180 - angle.degrees;
  }
  top ??= CROSSING_DEFAULT;
  return [top, 180 - top, top, 180 - top];
}

// Unknown angles share what is left over equally. A picture with every size
// given is stretched to close up when its sizes do not add up (a picture
// with a mistake to find).
function sharedSizes(angles: AngleLabel[], whole: number): number[] | null {
  let given = 0;
  let unknown = 0;
  for (const angle of angles) {
    if (angle.kind === 'none') return null;
    if (angle.kind === 'size') given += angle.degrees;
    else unknown += 1;
  }
  if (unknown > 0 && given >= whole) return null;
  const share = unknown > 0 ? (whole - given) / unknown : 0;
  const stretch = unknown > 0 ? 1 : whole / given;
  return angles.map((angle) =>
    angle.kind === 'size' ? angle.degrees * stretch : share,
  );
}

export function readAnglesAtPoint(
  record: Record<string, unknown>,
): AnglesAtPointSpec | null {
  const { layout } = record;
  if (
    layout !== 'point' &&
    layout !== 'line' &&
    layout !== 'crossing' &&
    layout !== 'triangle' &&
    layout !== 'quadrilateral'
  )
    return null;
  if (!Array.isArray(record.angles)) return null;
  const angles: AngleLabel[] = [];
  for (const entry of record.angles as unknown[]) {
    const label = readLabel(entry);
    if (label === null) return null;
    angles.push(label);
  }
  const count =
    layout === 'point' || layout === 'line'
      ? angles.length >= 2 && angles.length <= MAX_ANGLES[layout]
      : angles.length === (layout === 'triangle' ? 3 : 4);
  if (!count) return null;
  const drawn =
    layout === 'crossing'
      ? crossingSizes(angles)
      : sharedSizes(angles, WHOLE[layout]);
  if (drawn === null) return null;
  // A corner of 180° or more is not a corner.
  if (
    (layout === 'triangle' || layout === 'quadrilateral') &&
    drawn.some((size) => size >= 180)
  )
    return null;
  return { type: 'angles_at_point', layout, angles, drawn };
}

// The crossing line leans right unless a size says otherwise.
const TRANSVERSAL_DEFAULT = 115;
// A slant outside this cannot be drawn on a phone.
const TRANSVERSAL_RANGE = { least: 25, most: 155 };

export function readTransversal(
  record: Record<string, unknown>,
): TransversalSpec | null {
  if (!Array.isArray(record.angles) || record.angles.length !== 8) return null;
  const angles: AngleLabel[] = [];
  const names = new Set<string>();
  for (const entry of record.angles as unknown[]) {
    if (typeof entry === 'string' && /^[1-8]$/.test(entry.trim())) {
      const name = entry.trim();
      if (names.has(name)) return null;
      names.add(name);
      angles.push({ kind: 'name', name });
      continue;
    }
    const label = readLabel(entry);
    if (label === null) return null;
    angles.push(label);
  }
  // Angles 1, 3, 5 and 7 are the same size; the others make 180° with them.
  // Every size given must say the same, since the picture is true size.
  let slant: number | null = null;
  for (const [index, angle] of angles.entries()) {
    if (angle.kind !== 'size') continue;
    const size = index % 2 === 0 ? angle.degrees : 180 - angle.degrees;
    if (slant !== null && Math.abs(slant - size) > 1e-9) return null;
    slant = size;
  }
  slant ??= TRANSVERSAL_DEFAULT;
  if (slant < TRANSVERSAL_RANGE.least || slant > TRANSVERSAL_RANGE.most)
    return null;
  return { type: 'transversal', angles, slant };
}

// The label as it is drawn. An unknown angle is its letter or "?", never
// its size.
export function angleLabelText(label: AngleLabel): string {
  if (label.kind === 'size') return `${String(label.degrees)}°`;
  if (label.kind === 'unknown') return label.letter ?? '?';
  if (label.kind === 'name') return label.name;
  return '';
}

function spokenLabel(label: AngleLabel): string {
  if (label.kind === 'size')
    return label.degrees === 1
      ? '1 degree'
      : `${String(label.degrees)} degrees`;
  if (label.kind === 'name') return `angle ${label.name}`;
  if (label.kind === 'none') return 'an angle with no label';
  return label.letter === null
    ? 'an unknown angle'
    : `an unknown angle ${label.letter}`;
}

// What a screen reader says. It gives the sizes written on the picture and
// calls every other angle unknown. It never says how big an unknown angle
// is, or what kind of angle anything is, which is usually the answer.
export function describeAngle(angle: AngleSpec): string {
  const lines = angle.protractor
    ? 'Two lines meet at a point, with a protractor laid over them.'
    : 'Two lines meet at a point.';
  const which = angle.reflex
    ? 'The larger angle, round the outside of them,'
    : 'The angle between them';
  const what =
    angle.label.kind === 'size'
      ? `is marked ${spokenLabel(angle.label)}`
      : angle.label.letter === null
        ? 'is unknown'
        : `is unknown and is marked ${angle.label.letter}`;
  return `${lines} ${which} ${what}.`;
}

export function describeAnglesAtPoint(picture: AnglesAtPointSpec): string {
  const count = String(picture.angles.length);
  const list = picture.angles.map(spokenLabel).join(', ');
  switch (picture.layout) {
    case 'point':
      return `${count} angles meet at a point and go all the way round it. Going clockwise: ${list}.`;
    case 'line':
      return `${count} angles sit side by side on a straight line. From left to right: ${list}.`;
    case 'crossing':
      return `Two straight lines cross and make 4 angles. Going clockwise from the top: ${list}.`;
    case 'triangle':
      return `A triangle. Its angles, going clockwise from the top: ${list}.`;
    case 'quadrilateral':
      return `A shape with 4 straight sides. Its angles, going clockwise from the top left: ${list}.`;
  }
}

// Never the name of an angle pair (corresponding, alternate, co-interior):
// that is what the questions ask (wording approved by the owner, 1 Nov 2026).
export function describeTransversal(picture: TransversalSpec): string {
  const list = (from: number) =>
    picture.angles
      .slice(from, from + 4)
      .map(spokenLabel)
      .join(', ');
  return `Two parallel lines, one above the other, are crossed by a third straight line. Where it crosses the top line, the angles going clockwise from the top left are: ${list(0)}. Where it crosses the bottom line, going clockwise from the top left: ${list(4)}.`;
}

// ---------------------------------------------------------------------------
// Where everything is drawn. Measured in the drawing's own units, which are
// one pixel each when the picture is shown at its natural width.

export type AnglePoint = { x: number; y: number };
// A label's box, centred on (x, y).
export type AnglePictureLabel = AnglePoint & {
  text: string;
  width: number;
  height: number;
};
export type AnglePicture = {
  // Rays from a point, or the sides of a shape.
  lines: { from: AnglePoint; to: AnglePoint }[];
  // An arc, or a small square for a right angle that is marked 90°.
  marks: {
    square: boolean;
    points: AnglePoint[];
    radius: number;
    large: boolean;
  }[];
  labels: AnglePictureLabel[];
  // The point the rays leave from. A shape has none.
  vertex: AnglePoint | null;
  // The radius of the protractor laid over the angle.
  protractor: number | null;
  box: { x: number; y: number; width: number; height: number };
};

const RAY = 84;
const ARC = 19;
const SQUARE = 12;
const GAP = 3;
const PAD = 6;
export const ANGLE_LABEL_SIZE = 14;
const LABEL_HEIGHT = 15;
const PROTRACTOR = 130;
// A shape is drawn to fit inside this.
const SHAPE_WIDTH = 170;
const SHAPE_HEIGHT = 120;

// A bearing is in degrees clockwise from straight up.
function toward(bearing: number): AnglePoint {
  const turn = (bearing * Math.PI) / 180;
  return { x: Math.sin(turn), y: -Math.cos(turn) };
}

function along(from: AnglePoint, way: AnglePoint, reach: number): AnglePoint {
  return { x: from.x + way.x * reach, y: from.y + way.y * reach };
}

// Roughly how wide a label is drawn, to keep it off the lines.
function labelWidth(text: string): number {
  const signs = text.split('°').length - 1;
  return (text.length - signs) * 8.6 + signs * 6;
}

// How far a label's box reaches from its middle in one direction.
function reachOf(width: number, way: AnglePoint): number {
  return (Math.abs(way.x) * width + Math.abs(way.y) * LABEL_HEIGHT) / 2;
}

function overlaps(a: AnglePictureLabel, b: AnglePictureLabel): boolean {
  return (
    Math.abs(a.x - b.x) < (a.width + b.width) / 2 + GAP &&
    Math.abs(a.y - b.y) < (a.height + b.height) / 2 + GAP
  );
}

type Sector = { from: number; size: number; label: AngleLabel };

function isRightAngle(sector: Sector): boolean {
  return (
    sector.label.kind === 'size' &&
    sector.label.degrees === 90 &&
    Math.abs(sector.size - 90) < 0.01
  );
}

// The arc or square in the corner at `corner`, turning clockwise from
// `sector.from`.
function markOf(
  corner: AnglePoint,
  sector: Sector,
  radius: number,
): AnglePicture['marks'][number] {
  const first = toward(sector.from);
  const last = toward(sector.from + sector.size);
  if (isRightAngle(sector))
    return {
      square: true,
      points: [
        along(corner, first, SQUARE),
        along(corner, { x: first.x + last.x, y: first.y + last.y }, SQUARE),
        along(corner, last, SQUARE),
      ],
      radius: 0,
      large: false,
    };
  return {
    square: false,
    points: [along(corner, first, radius), along(corner, last, radius)],
    radius,
    large: sector.size > 180,
  };
}

// Angles sharing one point. Each label sits on the line down the middle of
// its angle, just far enough out for its box to fit between the two rays.
// In a narrow angle that is past the ends of the rays.
function fan(
  sectors: Sector[],
  rays: number[],
  reach: number,
  protractor: number | null,
): AnglePicture {
  const vertex = { x: 0, y: 0 };
  const marked = sectors.filter((sector) => sector.label.kind !== 'none');
  const marks = marked.map((sector, index) =>
    // Neighbours' arcs are told apart by sitting at two distances.
    markOf(
      vertex,
      sector,
      marked.length > 1 && index % 2 === 1 ? ARC + 5 : ARC,
    ),
  );
  const labels: AnglePictureLabel[] = [];
  for (const [index, sector] of marked.entries()) {
    const text = angleLabelText(sector.label);
    const width = labelWidth(text);
    const middle = toward(sector.from + sector.size / 2);
    const clear = (marks[index]?.square ? SQUARE * 1.5 : ARC + 5) + GAP;
    let out = clear + reachOf(width, middle);
    if (sector.size < 180) {
      const half = (sector.size * Math.PI) / 360;
      const across = Math.max(
        ...[sector.from, sector.from + sector.size].map((bearing) =>
          reachOf(width, toward(bearing + 90)),
        ),
      );
      const between = (across + GAP) / Math.sin(half);
      const beyond = reach + GAP + reachOf(width, middle);
      out = Math.max(out, Math.min(between, beyond));
    }
    const place = (): AnglePictureLabel => ({
      ...along(vertex, middle, out),
      text,
      width,
      height: LABEL_HEIGHT,
    });
    // Two narrow neighbours: the later one steps further out.
    while (labels.some((other) => overlaps(other, place()))) out += GAP;
    labels.push(place());
  }
  const lines = rays.map((bearing) => ({
    from: vertex,
    to: along(vertex, toward(bearing), reach),
  }));
  const picture = { lines, marks, labels, vertex, protractor };
  return { ...picture, box: boxOf(picture) };
}

// A shape with the given corners, clockwise. Its sides all touch one
// circle, which gives a shape for any set of angles.
function shape(sizes: number[], labels: AngleLabel[]): AnglePicture {
  const count = sizes.length;
  // The side after the last-but-one corner lies flat along the bottom.
  const flat = count === 3 ? 1 : 2;
  const turns = sizes.map((size) => 180 - size);
  // Which way each side faces. Side i runs from corner i to corner i + 1.
  const faces = Array<number>(count).fill(180);
  for (let step = 1; step < count; step += 1) {
    const index = (flat + step) % count;
    const before = (index - 1 + count) % count;
    faces[index] = (faces[before] ?? 0) + (turns[index] ?? 0);
  }
  const raw = turns.map((turn, index) =>
    along(
      { x: 0, y: 0 },
      toward((faces[index] ?? 0) - turn / 2),
      1 / Math.cos((turn * Math.PI) / 360),
    ),
  );
  const xs = raw.map((point) => point.x);
  const ys = raw.map((point) => point.y);
  const scale = Math.min(
    SHAPE_WIDTH / (Math.max(...xs) - Math.min(...xs)),
    SHAPE_HEIGHT / (Math.max(...ys) - Math.min(...ys)),
  );
  const corners = raw.map((point) => ({
    x: point.x * scale,
    y: point.y * scale,
  }));
  const marks: AnglePicture['marks'] = [];
  const placed: AnglePictureLabel[] = [];
  for (const [index, corner] of corners.entries()) {
    const label = labels[index];
    const size = sizes[index];
    if (!label || size === undefined || label.kind === 'none') continue;
    // The corner opens toward the middle of the circle, which is at 0, 0.
    const length = Math.hypot(corner.x, corner.y);
    const outward = { x: corner.x / length, y: corner.y / length };
    const inward = (Math.atan2(-corner.x, corner.y) * 180) / Math.PI;
    marks.push(
      markOf(corner, { from: inward - size / 2, size, label }, ARC - 5),
    );
    // The label sits outside the corner, clear of both sides.
    const text = angleLabelText(label);
    const width = labelWidth(text);
    placed.push({
      ...along(corner, outward, GAP + 2 + reachOf(width, outward)),
      text,
      width,
      height: LABEL_HEIGHT,
    });
  }
  const lines = corners.map((corner, index) => ({
    from: corner,
    to: corners[(index + 1) % count] ?? corner,
  }));
  const picture = {
    lines,
    marks,
    labels: placed,
    vertex: null,
    protractor: null,
  };
  return { ...picture, box: boxOf(picture) };
}

function boxOf(picture: Omit<AnglePicture, 'box'>): AnglePicture['box'] {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const line of picture.lines) {
    xs.push(line.from.x, line.to.x);
    ys.push(line.from.y, line.to.y);
  }
  for (const label of picture.labels) {
    xs.push(label.x - label.width / 2, label.x + label.width / 2);
    ys.push(label.y - label.height / 2, label.y + label.height / 2);
  }
  for (const mark of picture.marks)
    if (!mark.square && picture.vertex) {
      // An arc may bulge past both of its ends.
      xs.push(picture.vertex.x - mark.radius, picture.vertex.x + mark.radius);
      ys.push(picture.vertex.y - mark.radius, picture.vertex.y + mark.radius);
    }
  if (picture.protractor !== null) {
    xs.push(-picture.protractor, picture.protractor);
    ys.push(-picture.protractor);
  }
  const x = Math.min(...xs) - PAD;
  const y = Math.min(...ys) - PAD;
  return {
    x,
    y,
    width: Math.max(...xs) + PAD - x,
    height: Math.max(...ys) + PAD - y,
  };
}

// The gap between the parallel lines, how far each line runs past the
// crossing line, and how far the crossing line runs past each of them.
const PARALLEL_GAP = 104;
const PARALLEL_RUN = 112;
const CROSSING_RUN = 62;
// The arrows that show the lines are parallel.
const ARROW = { back: 7, wide: 5, from: 22 };

function shifted(picture: AnglePicture, by: AnglePoint): AnglePicture {
  const move = (point: AnglePoint) => ({
    x: point.x + by.x,
    y: point.y + by.y,
  });
  return {
    ...picture,
    lines: picture.lines.map((line) => ({
      from: move(line.from),
      to: move(line.to),
    })),
    marks: picture.marks.map((mark) => ({
      ...mark,
      points: mark.points.map(move),
    })),
    labels: picture.labels.map((label) => ({ ...label, ...move(label) })),
    vertex: picture.vertex && move(picture.vertex),
    box: { ...picture.box, x: picture.box.x + by.x, y: picture.box.y + by.y },
  };
}

function joinedBox(
  boxes: AnglePicture['box'][],
  lines: AnglePicture['lines'],
): AnglePicture['box'] {
  // A crossing with no angle marked has nothing to put in a box.
  const drawn = boxes.filter((box) => Number.isFinite(box.width));
  const xs = drawn.flatMap((box) => [box.x, box.x + box.width]);
  const ys = drawn.flatMap((box) => [box.y, box.y + box.height]);
  for (const line of lines) {
    xs.push(
      line.from.x - PAD,
      line.to.x - PAD,
      line.from.x + PAD,
      line.to.x + PAD,
    );
    ys.push(
      line.from.y - PAD,
      line.to.y - PAD,
      line.from.y + PAD,
      line.to.y + PAD,
    );
  }
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

function transversalPicture(picture: TransversalSpec): AnglePicture {
  const { slant } = picture;
  // Which way the crossing line points, going up, as a bearing.
  const bearing = slant - 90;
  const up = toward(bearing);
  // From the top crossing to the bottom one.
  const across = (PARALLEL_GAP * up.x) / -up.y;
  const top = { x: across / 2, y: -PARALLEL_GAP / 2 };
  const bottom = { x: -across / 2, y: PARALLEL_GAP / 2 };
  const span = Math.abs(across) / 2 + PARALLEL_RUN;
  const crossings = [top, bottom].map((vertex, index) => {
    const labels = picture.angles.slice(index * 4, index * 4 + 4);
    const sectors: Sector[] = [
      { from: 270, size: slant },
      { from: bearing, size: 180 - slant },
      { from: 90, size: slant },
      { from: bearing + 180, size: 180 - slant },
    ].map((sector, place) => ({
      ...sector,
      label: labels[place] ?? { kind: 'none' },
    }));
    return shifted(fan(sectors, [], CROSSING_RUN, null), vertex);
  });
  const arrow = (y: number): AnglePicture['lines'] => {
    const tip = { x: -span + ARROW.from, y };
    return [
      { from: { x: tip.x - ARROW.back, y: y - ARROW.wide }, to: tip },
      { from: { x: tip.x - ARROW.back, y: y + ARROW.wide }, to: tip },
    ];
  };
  const lines = [
    { from: { x: -span, y: top.y }, to: { x: span, y: top.y } },
    { from: { x: -span, y: bottom.y }, to: { x: span, y: bottom.y } },
    {
      from: along(top, up, CROSSING_RUN),
      to: along(bottom, up, -CROSSING_RUN),
    },
    ...arrow(top.y),
    ...arrow(bottom.y),
  ];
  return {
    lines,
    marks: crossings.flatMap((crossing) => crossing.marks),
    labels: crossings.flatMap((crossing) => crossing.labels),
    vertex: null,
    protractor: null,
    box: joinedBox(
      crossings.map((crossing) => crossing.box),
      lines,
    ),
  };
}

export function anglePicture(
  picture: AngleSpec | AnglesAtPointSpec | TransversalSpec,
): AnglePicture {
  if (picture.type === 'transversal') return transversalPicture(picture);
  if (picture.type === 'angle') {
    // One line points right and the other is turned anticlockwise from it.
    const turned = 90 - picture.degrees;
    const sector = picture.reflex
      ? { from: 90, size: 360 - picture.degrees, label: picture.label }
      : { from: turned, size: picture.degrees, label: picture.label };
    return picture.protractor
      ? fan([sector], [turned, 90], PROTRACTOR + 16, PROTRACTOR)
      : fan([sector], [turned, 90], RAY + 12, null);
  }
  if (picture.layout === 'triangle' || picture.layout === 'quadrilateral')
    return shape(picture.drawn, picture.angles);
  // Round a point and along a line both start from the ray pointing left.
  // Where lines cross, the first angle is centred on straight up.
  let from = picture.layout === 'crossing' ? -(picture.drawn[0] ?? 0) / 2 : 270;
  const sectors: Sector[] = [];
  for (const [index, size] of picture.drawn.entries()) {
    sectors.push({
      from,
      size,
      label: picture.angles[index] ?? { kind: 'none' },
    });
    from += size;
  }
  const rays = sectors.map((sector) => sector.from);
  if (picture.layout === 'line') rays.push(90);
  return fan(sectors, rays, RAY, null);
}
